/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone"; // dependent on utc plugin
import { ALARM_SEVERITIES, getAlarmSeverityColor, normalizeAlarmSeverity } from "./AlarmColors";

dayjs.extend(utc);
dayjs.extend(timezone);

interface Alarm {
  severity?: string;
  count: number;
  assembly_uuid: string;
  source?: string;
}

interface Assembly {
  uuid: string;
  name: string;
}

interface Site {
  state: string;
  alarms: Alarm[];
}

export const toISOStringWithTimezone = (date: Date | string, tzString: string): string => {
  // time offset in date will be stripped off and replaced with the provided tzString
  return dayjs(date).tz(tzString, true).format();
};

export const decorateAlarms = (alarms: Alarm[], assemblies: Assembly[]): void => {
  alarms.forEach((alarm) => {
    let assembly = assemblies.find((assembly) => assembly.uuid === alarm.assembly_uuid);
    if (assembly) {
      alarm.source = assembly.name;
    }
  });
};

export const getMarkerColor = (site: Site): string => {
  const alarms = site.alarms;

  // if the site isn't operating, we don't care what the alarm counts are,
  // the indicator should always be grey.
  if (site.state.toLowerCase() !== "operating") {
    return "grey";
  }

  const worst = ALARM_SEVERITIES.find((severity) =>
    alarms?.some((alarm) => normalizeAlarmSeverity(alarm.severity) === severity && alarm.count > 0),
  );

  if (worst) {
    return getAlarmSeverityColor(worst);
  }
  return "#85B953";
};

export const getShortTimezone = (timeZone: string) => {
  const date = new Date();

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "short",
  });

  const parts = formatter.formatToParts(date);
  const timeZonePart = parts.find((part) => part.type === "timeZoneName");

  return timeZonePart ? timeZonePart.value : null;
};

export const convertTz = (date: Date | string, tzString: string): Date => {
  const inTz = new Date(
    (typeof date === "string" ? new Date(date) : date).toLocaleString("en-US", {
      timeZone: tzString,
    }),
  );
  return inTz;
};

export const getAlarmFormattedTimeString = (date: string, timezone: string | null) => {
  if (timezone) {
    const dateTimezone = convertTz(date, timezone);
    const shortTimezoneString = getShortTimezone(timezone);
    const dateFormatted = dateTimezone.toLocaleTimeString("en-US", {
      hour12: false,
    });
    return dateFormatted + " " + shortTimezoneString;
  } else {
    return new Date(date).toLocaleTimeString("en-US", {
      hour12: false,
    });
  }
};

export const getAlarmFormattedDateString = (date: string, timezone?: string) => {
  if (timezone) {
    return convertTz(date, timezone).toLocaleDateString("en-US", {
      hour12: false,
    });
  } else {
    return new Date(date).toLocaleDateString("en-US", {
      hour12: false,
    });
  }
};

export const getCurrentTimeInTimezone = (timezone: string) => {
  const localDate = dayjs();
  const convertedDate = localDate.tz(timezone);
  const formattedDate = convertedDate.format("YYYY-MM-DDTHH:mm:ssZ");
  return formattedDate;
};

/**
 * Get timezone abbreviation for a given timezone.
 * @param {string|null} timezone - The timezone string (e.g., "America/Chicago").
 * @returns {string|null} - The abbreviation (e.g., "CST", "MST") or null if the input is null or invalid.
 */
export const getTimezoneAbbreviation = (timezone: string) => {
  if (!timezone) {
    return null; // Return null for null or undefined timezone
  }
  try {
    const now = dayjs().tz(timezone); // Get current time in the specified timezone
    return now.format("z"); // Return the abbreviation
  } catch (error) {
    console.error(`Invalid timezone: ${timezone}`, error);
    return null; // Return null if an invalid timezone is passed
  }
};

export const getUnixTimeMs = (localizedTimeStringNoTimezone: string, timezoneString: string) => {
  const zonedTime = dayjs.tz(localizedTimeStringNoTimezone, timezoneString);
  const unixTimeMs = zonedTime.unix() * 1000;

  return unixTimeMs;
};

/**
 * Finds the closest number in an array to a given target number.
 * @param {number[]} arr - The array of numbers to search through
 * @param {number} n - The target number to find the closest match for
 * @returns {number} - The closest number from the array
 */
export const closest = (arr: number[], n: number): number =>
  arr.sort((a, b) => Math.abs(a - n) - Math.abs(b - n))[0];
